import {ClientManagementLayout} from "../../layouts/ClientManagementLayout";
import {Pane} from "evergreen-ui";
import {SortBy} from "../../../common";
import {reduceRelayPaginationAction, RelayPaginationActionPayload, RelayPaginationState} from "../../pagination";
import {SortOrder} from "../../../placement/domains";
import React from "react";
import {AnticipatedDischargeInfo, AnticipatedDischargesFilterValue, AnticipatedDischargesPageState} from "./domain";
import {AnticipatedDischargeList} from "./AnticipatedDischargeList";

const defaultPageSize = 10;

export type AnticipatedDischargePageAction =
    | { type: 'SET_SORT_BY'; sortBy: SortBy<AnticipatedDischargeInfo> }
    | { type: 'SET_FILTER'; filter: AnticipatedDischargesFilterValue }
    | { type: 'SET_SEARCH'; search: string }
    | { type: 'SET_PAGE_SIZE'; pageSize: number }
    | { type: 'SET_MULTI_SELECT_TOGGLE'; item: AnticipatedDischargeInfo }
    | { type: 'SET_MULTI_SELECT_TOGGLE_ALL'; items: AnticipatedDischargeInfo[] }
    | {
    type: 'SET_DISCHARGE_MODAL_STATE';
    state: 'hide' | 'confirm' | 'update';
    selectedItems: AnticipatedDischargeInfo[]
}
    | { type: 'CLEAR_SELECTED' }
    | RelayPaginationActionPayload

function initState(): AnticipatedDischargesPageState {
    const defaultPagination = new RelayPaginationState('0', defaultPageSize)
    return {
        selectedUnconfirmedADs: [],
        pagination: defaultPagination,
        pageSize: defaultPagination.size,
        search: "",
        filter: {
            clientName: "",
            anticipatedDischargeDateFrom: null,
            anticipatedDischargeDateTo: null,
            dischargeNotificationDateFrom: null,
            dischargeNotificationDateTo: null,
            status: "Open"
        },
        sortBy: {
            column: "anticipatedDischargeDate",
            order: SortOrder.ASC,
        },
        dischargeModalState: 'hide',
        dischargeModalSelectedItems: []
    }
}

function reducer(
    state: AnticipatedDischargesPageState,
    action: AnticipatedDischargePageAction
): AnticipatedDischargesPageState {
    switch (action.type) {
        case "SET_SORT_BY":
            return {
                ...state,
                sortBy: action.sortBy,
                selectedUnconfirmedADs: [],
                pagination: new RelayPaginationState('0', state.pageSize),
            };
        case "SET_FILTER":
            return {
                ...state,
                filter: action.filter,
                selectedUnconfirmedADs: [],
                pagination: new RelayPaginationState('0', state.pageSize),
            };
        case "SET_SEARCH":
            return {
                ...state,
                search: action.search,
                selectedUnconfirmedADs: [],
            };
        case "SET_PAGE_SIZE":
            return {
                ...state,
                pageSize: action.pageSize,
                pagination: new RelayPaginationState('0', action.pageSize)
            };
        case "SET_MULTI_SELECT_TOGGLE":
            if (action.item.providerConfirmedDischargeDate !== null) {
                return state
            }
            return {
                ...state,
                selectedUnconfirmedADs: toggleItem(state.selectedUnconfirmedADs, action.item)
            }
        case "SET_MULTI_SELECT_TOGGLE_ALL":
            const aItems = action.items
                .filter(v => v.providerConfirmedDischargeDate === null)
            const items = toggleItems(
                state.selectedUnconfirmedADs,
                aItems)
            return {
                ...state,
                selectedUnconfirmedADs: items,
            }
        case 'RELAY_PAGINATION_ACTION':
            return {
                ...state,
                pagination: reduceRelayPaginationAction(state.pagination, action)
            };
        case "SET_DISCHARGE_MODAL_STATE":
            if (action.state === 'hide') {
                return {
                    ...state,
                    dischargeModalState: action.state,
                    dischargeModalSelectedItems: []
                }
            }
            return {
                ...state,
                dischargeModalState: action.state,
                dischargeModalSelectedItems: action.selectedItems
            }
        case "CLEAR_SELECTED":
            return {
                ...state,
                selectedUnconfirmedADs: [],
            }
        default:
            return state;
    }
}

const toggleItem = (before: AnticipatedDischargeInfo[], toggleItem: AnticipatedDischargeInfo) => {
    const beforeIds = new Set(before.map((item) => item.guid));
    if (beforeIds.has(toggleItem.guid)) {
        return before.filter((item) => item.guid.localeCompare(toggleItem.guid));
    }

    return [...before, toggleItem];
}

const toggleItems = (before: AnticipatedDischargeInfo[], items: AnticipatedDischargeInfo[]) => {
    if (before.length !== 0) {
        return []
    }

    return [...items]
}

export const AnticipatedDischargePage = () => {
    const [{
        pagination,
        filter,
        sortBy,
        search,
        pageSize,
        selectedUnconfirmedADs,
        dischargeModalSelectedItems,
        dischargeModalState
    }, dispatch] = React.useReducer(reducer, undefined, initState);

    return (
        <ClientManagementLayout customSelection={'Anticipated Discharges'}>
            <Pane minHeight="calc(100vh - 100px - 260px - 36px)" flexGrow={1}>
                <AnticipatedDischargeList pagination={pagination}
                                          modalSelectedItems={dischargeModalSelectedItems}
                                          filter={filter}
                                          searchText={search}
                                          pageSize={pageSize}
                                          selectedUnconfirmedADs={selectedUnconfirmedADs}
                                          dischargeModalState={dischargeModalState}
                                          sortBy={sortBy}
                                          dispatch={dispatch}/>
            </Pane>
        </ClientManagementLayout>
    )
}
